import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  Grid,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useQueryClient } from "@tanstack/react-query";
import { DifferenceOutlined } from "@mui/icons-material";
import DownloadIcon from "@mui/icons-material/Download";
import ChatIcon from "@mui/icons-material/Chat";
import StopIcon from "@mui/icons-material/Stop";
import { marked } from "marked";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { downloadFile } from "../chatpage/api";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import useFileComparisonStore from "@/store/file-comparison-store";
import { deleteConversation } from "@/api/copilot/copilot.api";

import "./file-comparison.css";
import { ChatLoadingTimer } from "../ui/chat-loading-timer";
import { useChatTimerStore } from "@/store/chat-timer-store";
import useCopilotStore from "@/store/copilotStore";

const ACCEPTED_FILE_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "text/plain",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
];

const FileUploadBox = ({
  fileState,
  setFileState,
  label,
  setGcsPath,
  setUploaded,
}: any) => {
  const theme = useTheme();
  const [error, setError] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  const uploadFile = async (selectedFile: any) => {
    if (!selectedFile) return;

    setError("");
    setIsUploading(true);
    setUploaded(false);
    const formData = new FormData();
    formData.append("files", selectedFile);

    try {
      const accessToken = localStorage.getItem("accessToken");
      const userInfo = JSON.parse(localStorage?.getItem("user_data") ?? "");
      const uuid = userInfo.id;
      formData.append("project_name", " ");
      formData.append("user_id", uuid);
      formData.append("doc_type", "-");
      formData.append("file_types", selectedFile?.type || "pdf");
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/kb-documents-upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent: any) => {
            const progress = Math.round(
              (progressEvent.loaded / progressEvent?.total) * 100
            );
            // setUploadProgress(progress);
          },
        }
      );
      if (response.status === 200) {
        const data = await response.data;
        const docUrl = data.data[0].doc_url;
        setGcsPath(docUrl);
        setUploaded(true);
      } else {
        setError(`Upload failed: ${response.status}`);
        console.error("Upload failed:", response.status);
        setUploaded(false);
      }
    } catch (error) {
      setError(
        `Upload error: ${error instanceof Error ? error.message : "Unknown error"}`
      );
      console.error("Upload error:", error);
      setUploaded(false);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = useCallback(
    (event: any) => {
      const file = event.target.files?.[0];
      if (file && ACCEPTED_FILE_TYPES.includes(file.type)) {
        if (file.type.startsWith("image/")) {
          const reader = new FileReader();
          reader.onload = (e) =>
            setFileState({ file, preview: e.target?.result });
          reader.readAsDataURL(file);
        } else {
          setFileState({ file, preview: null });
        }
        uploadFile(file);
      } else if (file) {
        setError(
          "Unsupported file type. Please upload .pdf, .jpg, .png, .doc, .xls  or .txt files."
        );
        console.log(
          "Unsupported file type. Please upload .pdf, .jpg, .png, .doc, .xls or .txt files."
        );
      }
    },
    [setFileState]
  );

  const handleDrop = useCallback(
    (event: any) => {
      event.preventDefault();
      handleFileChange({ target: { files: event.dataTransfer.files } });
    },
    [handleFileChange]
  );

  return (
    <Paper
      elevation={0}
      sx={{
        border: "0.125rem dashed #ccc",
        borderRadius: "0.5rem",
        p: 4,
        height: 300,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        "&:hover": { borderColor: theme.palette.primary.main },
      }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
    >
      <input
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.txt,.doc,.docx,.xls,.xlsx"
        hidden
        onChange={handleFileChange}
        id={label}
      />
      <label
        htmlFor={label}
        style={{
          width: "100%",
          height: "100%",
          textAlign: "center",
          cursor: "pointer",
          paddingTop: "2.5rem",
        }}
      >
        {fileState.file ? (
          <Box textAlign="center">
            {fileState.preview ? (
              <Box
                component="img"
                src={fileState.preview}
                sx={{ maxHeight: 200, maxWidth: "100%", objectFit: "contain" }}
              />
            ) : (
              <Typography variant="h1" sx={{ fontSize: "4rem" }}>
                📄
              </Typography>
            )}
            <Typography variant="body1" sx={{ mt: 2 }}>
              {fileState.file.name}
            </Typography>
            {isUploading && (
              <Box mt={1}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary">
                  Uploading...
                </Typography>
              </Box>
            )}
            {error && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {error}
              </Typography>
            )}
          </Box>
        ) : (
          <>
            <CloudUploadIcon sx={{ fontSize: 60, color: "#888", mb: 2 }} />
            <Typography variant="h6" sx={{ color: theme.palette.primary.main }}>
              {label}
            </Typography>
            <Typography variant="h6" sx={{ color: "red" }}>
              (required)
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              align="center"
              sx={{ mt: 1 }}
              component="label"
              htmlFor={label}
            >
              Click to{" "}
              <Box component="span" sx={{ color: theme.palette.primary.main }}>
                browse
              </Box>{" "}
              or drag & drop
            </Typography>
            {error && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {error}
              </Typography>
            )}
          </>
        )}
      </label>
    </Paper>
  );
};

const FileComparison = () => {
  const [fileOne, setFileOne] = useState<any>({ file: null, preview: null });
  const [fileTwo, setFileTwo] = useState<any>({ file: null, preview: null });
  const [fileOneGcs, setFileOneGcs] = useState("");
  const [fileTwoGcs, setFileTwoGcs] = useState("");
  const [fileOneUploaded, setFileOneUploaded] = useState(false);
  const [fileTwoUploaded, setFileTwoUploaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [comparisonStatus, setComparisonStatus] = useState("PENDING");
  const [outputGcs, setOutputGcs] = useState("");
  const [conversationId, setConversationId] = useState("");
  const [content, setContent] = useState("");

  const isMobile = useMediaQuery(useTheme().breakpoints.down("sm"));

  const cancelTokenRef = useRef(null);

  const navigate = useNavigate();
  const fileComparisonStore = useFileComparisonStore();
  const { startGeneration, stopGeneration } = useChatTimerStore();
  const { resetConversation } = useCopilotStore();
  const queryClient = useQueryClient();

  const handleCompare = async () => {
    if (fileOne.file && fileTwo.file) {
      console.log(
        "Comparing files:",
        fileOne.file?.name,
        "and",
        fileTwo.file?.name
      );
    } else {
      console.log("Please upload both files before comparing.");
      return;
    }

    try {
      const accessToken = localStorage.getItem("accessToken");
      const userInfo = JSON.parse(localStorage?.getItem("user_data") ?? "");
      const uuid = userInfo.id;
      const data = {
        attachments: {
          file1: fileOneGcs,
          file2: fileTwoGcs,
          additional_files: [],
        },
        source_ids: [],
        project_name: "G7",
        conversation_id: "",
        display_message: false,
        message: "compare",
        user_id: uuid,
        section: "compare_files",
        process: "init",
        version_uri: "",
        product: ["Dexcom G7"],
        tool_list: [],
        additional_prompt: "",
      };

      setLoading(true);
      setComparisonStatus("PROCESSING");

      const CancelToken = axios.CancelToken;
      const source = CancelToken.source();
      cancelTokenRef.current = source;

      startGeneration();

      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/chat-stream`,
        data,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          cancelToken: source.token,
          responseType: "text",
          // timeout: 600000,
          timeout: 1800000,
          onDownloadProgress: (progressEvent) => {
            const responseText = progressEvent.event?.currentTarget?.response;

            if (responseText) {
              const chunks = responseText.split("data:");

              for (const chunk of chunks) {
                if (!chunk.trim()) continue;

                try {
                  const parsedChunk = JSON.parse(chunk);

                  if (parsedChunk.conversation_id) {
                    setConversationId(parsedChunk.conversation_id);
                    fileComparisonStore.setConversationId(
                      parsedChunk.conversation_id
                    );
                  }

                  if (parsedChunk.internal_event) {
                    console.log("Internal event:", parsedChunk.internal_event);
                  }

                  if (parsedChunk.data && parsedChunk.data.status) {
                    setComparisonStatus(parsedChunk.data.status);
                  }

                  if (parsedChunk.data && parsedChunk.data.response) {
                    setContent(parsedChunk.data.response);
                    setComparisonStatus("DONE");
                    queryClient.invalidateQueries({ queryKey: ["chatHistory"] });

                    if (parsedChunk.data.gcs_generated_documents) {
                      setOutputGcs(parsedChunk.data.gcs_generated_documents);
                    }

                    if (parsedChunk.data.conversation_id) {
                      setConversationId(parsedChunk.data.conversation_id);
                      fileComparisonStore.setConversationId(
                        parsedChunk.data.conversation_id
                      );
                    }
                  }
                } catch (parseError) {
                  console.error("Error parsing chunk:", parseError);
                }
              }
            }
          },
        }
      );

      if (response.status !== 200) {
        console.error("File comparison API failed:", response.status);
        setComparisonStatus("FAILED");
      }
    } catch (error: any) {
      if (axios.isCancel(error)) {
        console.log("Request canceled:", error.message);
        setComparisonStatus("CANCELLED");
      } else if (error.message && error.message.includes("timeout")) {
        console.error("File comparison api timeout:", error.message);
        setComparisonStatus("TIMEOUT");
      } else {
        console.error("API error:", error);
        setComparisonStatus("FAILED");
      }
    } finally {
      setLoading(false);
      stopGeneration();
    }
  };

  const startChat = () => {
    resetConversation();
    navigate(`/copilot?convType=compare&conversationId=${conversationId}`);
  };

  const handleStop = async () => {
    if (fileComparisonStore.conversationId) {
      await deleteConversation(fileComparisonStore.conversationId);
    }
    if (cancelTokenRef.current) {
      (cancelTokenRef.current as any)?.cancel("Operation canceled by the user");
      setLoading(false);
      setComparisonStatus("CANCELLED");
    }
  };

  const downloadContent = async () => {
    let htmlCode = marked(content);
    const fileName = `response_${fileComparisonStore.conversationId}`;
    const fileType = "pdf";
    await downloadFile(htmlCode, fileType, fileName);
  };

  useEffect(() => {
    return () => {
      if (cancelTokenRef.current) {
        (cancelTokenRef.current as any)?.cancel(
          "Operation canceled by the user"
        );
      }
      fileComparisonStore.setStatus("START");
      setFileOne({ file: null, preview: null });
      setFileTwo({ file: null, preview: null });
    };
  }, [fileComparisonStore.sessionId]);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography
        variant="h5"
        align="center"
        fontWeight={"600"}
        color={"red"}
        gutterBottom
      >
        Supported files: .pdf, .jpg, .png, .txt, .doc, .xls
      </Typography>

      <Grid container spacing={3} justifyContent="center">
        <Grid item xs={12} md={6}>
          <FileUploadBox
            fileState={fileOne}
            setFileState={setFileOne}
            label="Upload File 1 *"
            setGcsPath={setFileOneGcs}
            setUploaded={setFileOneUploaded}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FileUploadBox
            fileState={fileTwo}
            setFileState={setFileTwo}
            label="Upload File 2 *"
            setGcsPath={setFileTwoGcs}
            setUploaded={setFileTwoUploaded}
          />
        </Grid>
      </Grid>
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4, gap: 2 }}>
        {["PENDING", "CANCELLED"].includes(comparisonStatus) &&
          !loading &&
          !fileComparisonStore.uploadErrors && (
            <Button
              variant="contained"
              size="large"
              onClick={handleCompare}
              disabled={
                !fileOne.file ||
                !fileTwo.file ||
                !fileOneUploaded ||
                !fileTwoUploaded
              }
              sx={{
                py: 1.5,
                px: 8,
                borderRadius: "3rem",
                backgroundColor: "#137D00",
                "&:hover": { backgroundColor: "var(--colors-brand-tertiary)" },
                width: isMobile ? "100%" : "auto",
              }}
            >
              <DifferenceOutlined />
              Compare
            </Button>
          )}
        {loading && (
          <Button
            disabled={!fileComparisonStore.conversationId}
            variant="contained"
            size="large"
            onClick={handleStop}
            sx={{
              py: 1.5,
              px: 8,
              borderRadius: "3rem",
              backgroundColor: "#D32F2F",
              "&:hover": { backgroundColor: "#B71C1C" },
              width: isMobile ? "100%" : "auto",
            }}
          >
            <StopIcon />
            Stop
          </Button>
        )}
        {["DONE"].includes(comparisonStatus) && (
          <div className="flex-col justify-center align-middle gap-2">
            <div className="flex gap-3">
              <Button
                variant="contained"
                size="large"
                onClick={startChat}
                sx={{
                  py: 1.5,
                  px: 8,
                  borderRadius: "3rem",
                  backgroundColor: "#137D00",
                  "&:hover": {
                    backgroundColor: "var(--colors-brand-tertiary)",
                  },
                  width: isMobile ? "100%" : "auto",
                }}
              >
                <ChatIcon />
                Chat
              </Button>
              <Button
                variant="contained"
                size="large"
                onClick={downloadContent}
                disabled={!fileOne.file || !fileTwo.file}
                sx={{
                  py: 1.5,
                  px: 8,
                  borderRadius: "3rem",
                  backgroundColor: "#137D00",
                  "&:hover": {
                    backgroundColor: "var(--colors-brand-tertiary)",
                  },
                  width: isMobile ? "100%" : "auto",
                }}
              >
                <DownloadIcon />
                Download
              </Button>
              <Sheet>
                <SheetTrigger>
                  <div className="flex justify-center">
                    <Button
                      variant="contained"
                      size="large"
                      sx={{
                        py: 1.5,
                        px: 8,
                        borderRadius: "3rem",
                        backgroundColor: "#137D00",
                        "&:hover": {
                          backgroundColor: "var(--colors-brand-tertiary)",
                        },
                        width: isMobile ? "100%" : "auto",
                      }}
                    >
                      <VisibilityIcon />
                      View
                    </Button>
                  </div>
                </SheetTrigger>
                <SheetContent style={{ maxWidth: "50vw" }}>
                  <SheetHeader>
                    <SheetTitle>Output</SheetTitle>
                    <SheetDescription>
                      <div
                        className="sheet-view max-h-[80vh] overflow-auto"
                        dangerouslySetInnerHTML={{ __html: marked(content) }}
                      ></div>
                    </SheetDescription>
                  </SheetHeader>
                </SheetContent>
              </Sheet>
            </div>

            <div className="flex justify-center mt-3">
              <CheckCircleIcon
                fontSize={"large"}
                sx={{
                  color: "var(--colors-brand-secondary)",
                }}
              />
            </div>
          </div>
        )}
      </Box>
      {comparisonStatus === "TIMEOUT" && (
        <Box sx={{ color: "red" }}>Response Timeout</Box>
      )}
      <div className="min-h-6 w-full flex justify-center align-middle">
        <ChatLoadingTimer className="text-gray-600" />
      </div>
      {loading && (
        <div className="text-center">
          <Box sx={{ width: "100%", my: 2, borderRadius: 3 }}>
            <Typography variant="body1" sx={{ mt: 2 }}>
              {comparisonStatus === "PROCESSING" && "Processing data..."}
              {comparisonStatus === "Comparing Files..." &&
                "Comparing files..."}
              {comparisonStatus === "completed" && "Completed processing"}
              {comparisonStatus ===
                "Preparing your results, fetching generated content..." &&
                "Preparing your results, fetching generated content..."}
            </Typography>
          </Box>
        </div>
      )}
    </Container>
  );
};

export default FileComparison;
